import * as React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Kbd } from './hotkeys';

/**
 * Single source of truth for the actions exposed on a polygon entity:
 * — sidebar three-dots popover and the canvas edit toolbar both render
 * this list. Adding a new action propagates to both surfaces by editing
 * one place.
 */
export type EntityActionKey = 'edit' | 'delete';

export interface EntityActionDef {
  key: EntityActionKey;
  /** i18n key for the label. */
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: 'default' | 'destructive';
  /** Hotkey badge on the toolbar button (handled by EditToolbar/CanvasRenderer). */
  hotkey: string;
}

export const ENTITY_ACTIONS: readonly EntityActionDef[] = [
  { key: 'edit',   labelKey: 'drawing.edit',   icon: Pencil, variant: 'default',     hotkey: 'E' },
  { key: 'delete', labelKey: 'drawing.delete', icon: Trash2, variant: 'destructive', hotkey: 'Del' },
];

export interface EntityActionHandlers {
  onAction: (key: EntityActionKey) => void;
  /** When true, action is hidden (e.g. edit unavailable when no detail form). */
  isAvailable?: (key: EntityActionKey) => boolean;
  disabled?: boolean;
}

interface EntityActionListProps extends EntityActionHandlers {
  t: (key: string) => string;
  /** "menu" → vertical popover items.  "toolbar" → horizontal buttons. */
  layout: 'menu' | 'toolbar';
  className?: string;
}

/** Shared renderer used by the three-dots popover (menu) and the canvas
 *  edit toolbar (toolbar). Iterates ENTITY_ACTIONS so both surfaces stay
 *  in sync — only the rendering shell differs. */
export function EntityActionList({
  layout, t, onAction, isAvailable, disabled, className,
}: EntityActionListProps) {
  const items = ENTITY_ACTIONS.filter(a => isAvailable?.(a.key) ?? true);
  if (items.length === 0) return null;

  if (layout === 'menu') {
    return (
      <div className={cn('flex flex-col py-1', className)} role="menu">
        {items.map(action => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              role="menuitem"
              type="button"
              disabled={disabled}
              // Stop the click from bubbling through the portal's React tree to
              // the sidebar row's onClick, which would re-run the row's
              // select-without-openDetails handler and clobber the menu
              // action's intent (e.g. Edit's openDetails:true → undefined).
              onClick={(e) => { e.stopPropagation(); onAction(action.key); }}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                'hover:bg-muted hover:text-foreground',
                action.variant === 'destructive' && 'text-destructive hover:bg-destructive/10 hover:text-destructive',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Icon className="size-4" />
              <span>{t(action.labelKey)}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Toolbar layout — horizontal buttons, used inside the EditToolbar.
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {items.map(action => {
        const Icon = action.icon;
        return (
          <Button
            key={action.key}
            type="button"
            size="sm"
            variant={action.variant === 'destructive' ? 'destructive' : 'outline'}
            disabled={disabled}
            onClick={() => onAction(action.key)}
          >
            <Icon className="size-3.5" />
            <span>{t(action.labelKey)}</span>
            <Kbd>{action.hotkey}</Kbd>
          </Button>
        );
      })}
    </div>
  );
}
